import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import {
  createApiKey,
  fetchApiKeys,
  revokeApiKey,
  type ApiKeyCreated,
  type ApiKeyRow,
} from '@/services/apiKeys';

const QUERY_KEY = ['api-keys'] as const;

/**
 * Personal API keys: read-only access to the data the signed-in account may read, for scripts
 * and statistical software.
 *
 * A key is shown ONCE, here, when it is created; the server keeps only its hash, so a lost
 * key is revoked and replaced rather than recovered. It authenticates as its owner (same
 * agencies) and can only read, so the page says what it can and cannot do before it hands one
 * out.
 */
export function ApiKeysPage() {
  const { t, i18n } = useTranslation(['common']);
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchApiKeys,
  });

  const create = useMutation({
    mutationFn: createApiKey,
    onSuccess: (key) => {
      setCreated(key);
      setName('');
      void client.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError && err.code === 'api_key_limit'
          ? t('common:apiKeys.limit')
          : t('common:apiKeys.createFailed'),
      ),
  });
  const revoke = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => void client.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: () => toast.error(t('common:apiKeys.revokeFailed')),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) create.mutate(name.trim());
  };
  const when = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(iso),
        )
      : '—';

  return (
    <div className="space-y-4 p-6" data-api-keys>
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('common:apiKeys.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('common:apiKeys.subtitle')}</p>
      </header>
      <p className="rounded-md border p-3 text-xs text-muted-foreground" data-key-rules>
        {t('common:apiKeys.rules')}
      </p>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('common:apiKeys.name')}
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={80}
            placeholder={t('common:apiKeys.namePlaceholder')}
            className="block w-64 rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </label>
        <button
          type="submit"
          disabled={!name.trim() || create.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
        >
          <KeyRound className="h-3.5 w-3.5" aria-hidden />
          {t('common:apiKeys.create')}
        </button>
      </form>

      {created ? <NewKey created={created} onDone={() => setCreated(null)} /> : null}

      {isPending ? <SkeletonLoader rows={3} /> : null}
      {error ? <ErrorMessage message={error.message} onRetry={() => void refetch()} /> : null}
      {data ? (
        <KeyTable
          keys={data.keys}
          max={data.max_active}
          when={when}
          onRevoke={(id) => revoke.mutate(id)}
        />
      ) : null}
    </div>
  );
}

function NewKey({ created, onDone }: { created: ApiKeyCreated; onDone: () => void }) {
  const { t } = useTranslation(['common']);
  const origin = window.location.origin;
  const curl = `curl -H "X-API-Key: ${created.key}" "${origin}/api/v1/cade/export/dataset" -o cade-dataset.csv`;
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common:apiKeys.copied'));
    } catch {
      toast.error(t('common:apiKeys.copyFailed'));
    }
  };
  return (
    <section
      className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
      role="alert"
      data-new-key
    >
      <p className="text-sm font-medium text-foreground">{t('common:apiKeys.shownOnce')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="break-all rounded bg-muted px-2 py-1 text-sm text-foreground">
          {created.key}
        </code>
        <button
          type="button"
          onClick={() => void copy(created.key)}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-foreground"
        >
          <Copy className="h-3 w-3" aria-hidden />
          {t('common:apiKeys.copy')}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{t('common:apiKeys.example')}</p>
      <pre className="overflow-x-auto rounded bg-muted p-2 text-xs text-foreground">{curl}</pre>
      <button type="button" onClick={onDone} className="text-xs text-primary underline">
        {t('common:apiKeys.done')}
      </button>
    </section>
  );
}

function KeyTable({
  keys,
  max,
  when,
  onRevoke,
}: {
  keys: ApiKeyRow[];
  max: number;
  when: (iso: string | null) => string;
  onRevoke: (id: string) => void;
}) {
  const { t } = useTranslation(['common']);
  if (keys.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('common:apiKeys.none')}</p>;
  }
  const active = keys.filter((k) => !k.revoked_at).length;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t('common:apiKeys.activeOf', { active, max })}
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs text-foreground">
            <tr>
              {['name', 'prefix', 'created', 'lastUsed', 'status'].map((c) => (
                <th key={c} className="px-3 py-2">
                  {t(`common:apiKeys.columns.${c}`)}
                </th>
              ))}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key.id} className="border-t" data-api-key-row>
                <td className="px-3 py-2 text-foreground">{key.name}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{key.prefix}…</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{when(key.created_at)}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {when(key.last_used_at)}
                </td>
                <td className="px-3 py-2 text-xs text-foreground">
                  {key.revoked_at
                    ? t('common:apiKeys.revokedOn', { date: when(key.revoked_at) })
                    : t('common:apiKeys.active')}
                </td>
                <td className="px-3 py-2 text-right">
                  {key.revoked_at ? null : (
                    <button
                      type="button"
                      onClick={() => onRevoke(key.id)}
                      aria-label={t('common:apiKeys.revoke', { name: key.name })}
                      className="rounded border p-1 text-foreground"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
